import React from "react";
import { graphql } from "gatsby";

import { Box, Stack } from "@mui/material";
import SiteBannerLayout from "@openstack_dev/gatsby-theme-marketing-oif-core/src/components/SiteBannerLayout";
import PropTypes from "prop-types";
import PageContainer from "@/components/PageContainer";
import NavButtons from "@/components/pages/Community/NavButtons";
import {
  Title,
  SubTitle,
  Content,
  PreTitle
} from "@/components/pages/Community";
import QuickLinksColumns from "@/components/pages/Community/QuickLinksColumns";
import WhereToStart from "@/components/pages/Community/WhereToStart";
import CommunityBanner from "@/components/pages/Community/CommunityBanner";
import HelpBanner from "@/components/pages/Community/HelpBanner";
import styles from "./CommunityPage.module.scss";

const CommunityPageTemplate = ({ data }) => {
  const {
    topBanner,
    contributePanel,
    quickLinks,
    whereToStart,
    helpBanner,
    communityBanner
  } = data;

  return (
    <SiteBannerLayout>
      <Stack sx={{ width: "100%" }}>
        <Stack
          sx={{
            paddingTop: { xs: 1, md: 3, lg: 6.25 },
            paddingBottom: { xs: 3, md: 3, lg: 6.25 },
            backgroundImage: `url(${topBanner.backgroundImage.publicURL})`
          }}
        >
          <PageContainer sx={{ textAlign: "center", color: "white" }}>
            <Title className={styles.topBannerTitle}>{topBanner.title}</Title>
            <SubTitle className={styles.topBannerDescription}>
              {topBanner.description}
            </SubTitle>
          </PageContainer>
        </Stack>
        <PageContainer
          sx={{
            color: "#427FA2",
            paddingTop: { xs: 1, md: 3, lg: 6.25 },
            paddingBottom: { xs: 1, md: 3, lg: 3.75 }
          }}
        >
          <Content
            sx={{
              textAlign: "center",
              paddingBottom: { xs: 2, md: 2, lg: 6.25 },
              zoom: { xs: 1.1 }
            }}
          >
            {contributePanel.title}
          </Content>
          <NavButtons contributePanel={contributePanel} />
        </PageContainer>
      </Stack>
      <Stack
        sx={{
          paddingTop: 6,
          paddingBottom: 10,
          color: "#2b4d68",
          background: "#F3F7FB"
        }}
      >
        <PageContainer>
          <PreTitle>{quickLinks.pretitle}</PreTitle>
          <Title
            sx={{ textAlign: "center", fontWeight: 400, fontSize: "31px" }}
          >
            {quickLinks.title}
          </Title>
          <QuickLinksColumns columns={quickLinks.columns} />
        </PageContainer>
      </Stack>
      <Stack
        sx={{
          backgroundImage: `url(${whereToStart.backgroundImage.publicURL})`,
          color: "white"
        }}
      >
        <PageContainer sx={{ paddingX: 0 }}>
          <WhereToStart whereToStart={whereToStart} />
        </PageContainer>
      </Stack>
      <PageContainer sx={{ paddingX: 0, paddingY: 8 }}>
        <Title
          sx={{
            textAlign: "center",
            fontWeight: 400,
            fontSize: "30px",
            color: "#2b4d68"
          }}
        >
          {helpBanner.title}
        </Title>
        <Content
          sx={{
            textAlign: "center",
            fontWeight: 400,
            paddingX: "18%",
            color: "#2b4d68",
            marginBottom: 6
          }}
        >
          {helpBanner.description}
        </Content>
        <HelpBanner helpBanner={helpBanner} />
      </PageContainer>
      <Stack sx={{ backgroundColor: "#2A4E68", color: "white" }}>
        <Box sx={{ width: "100%" }}>
          <img
            src={communityBanner.backgroundImage.publicURL}
            alt={communityBanner.backgroundImageAlt}
            style={{ width: "100%", height: "auto" }}
          />
        </Box>
        <PageContainer
          sx={{ paddingX: 2, paddingTop: 2, paddingBottom: 10, color: "white" }}
        >
          <Title
            sx={{
              textAlign: "center",
              fontWeight: 400,
              fontSize: "35px",
              paddingBottom: 4
            }}
          >
            {communityBanner.title}
          </Title>
          <Content
            sx={{ textAlign: "center", fontWeight: 400, marginBottom: 6 }}
          >
            {communityBanner.description}
          </Content>
          <CommunityBanner list={communityBanner.list} />
        </PageContainer>
      </Stack>
    </SiteBannerLayout>
  );
};

CommunityPageTemplate.propTypes = {
  data: PropTypes.shape({
    pageTitle: PropTypes.string,
    topBanner: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      backgroundImage: PropTypes.string
    }),
    contributePanel: PropTypes.shape({
      title: PropTypes.string,
      selectProject: PropTypes.shape({
        title: PropTypes.string,
        text: PropTypes.string
      }),
      unselect: PropTypes.shape({
        cta: PropTypes.string,
        onProjectsAddPreviousText: PropTypes.string
      }),
      navButtons: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          image: PropTypes.string,
          imageAlt: PropTypes.string,
          externalURL: PropTypes.string,
          items: PropTypes.arrayOf(
            PropTypes.shape({
              title: PropTypes.string,
              tooltip: PropTypes.string,
              options: PropTypes.arrayOf(
                PropTypes.shape({
                  name: PropTypes.string,
                  projects: PropTypes.arrayOf(
                    PropTypes.shape({
                      code_name: PropTypes.string,
                      mascot_image_url: PropTypes.string,
                      name: PropTypes.string,
                      documentation_link: PropTypes.string
                    })
                  )
                })
              )
            })
          )
        })
      )
    }),
    quickLinks: PropTypes.shape({
      pretitle: PropTypes.string,
      title: PropTypes.string,
      columns: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          links: PropTypes.arrayOf(
            PropTypes.shape({
              title: PropTypes.string,
              url: PropTypes.string
            })
          )
        })
      )
    }),
    whereToStart: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      backgroundImage: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string,
          url: PropTypes.string
        })
      )
    }),
    helpBanner: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      communityManagers: PropTypes.shape({
        title: PropTypes.string,
        list: PropTypes.arrayOf(
          PropTypes.shape({
            user: PropTypes.shape({
              id: PropTypes.number,
              first_name: PropTypes.string,
              last_name: PropTypes.string,
              state: PropTypes.string,
              country: PropTypes.string,
              pic: PropTypes.string,
              twitter: PropTypes.string,
              linked_in: PropTypes.string
            })
          })
        )
      }),
      ambassadors: PropTypes.shape({
        title: PropTypes.string,
        list: PropTypes.arrayOf(
          PropTypes.shape({
            user: PropTypes.shape({
              id: PropTypes.number,
              first_name: PropTypes.string,
              last_name: PropTypes.string,
              state: PropTypes.string,
              country: PropTypes.string,
              pic: PropTypes.string,
              twitter: PropTypes.string,
              linked_in: PropTypes.string
            })
          })
        )
      })
    }),
    communityBanner: PropTypes.shape({
      backgroundImage: PropTypes.string,
      backgroundImageAlt: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      list: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          links: PropTypes.arrayOf(
            PropTypes.shape({
              text: PropTypes.string,
              url: PropTypes.string
            })
          )
        })
      )
    })
  }).isRequired
};

export const query = graphql`
  query {
    communityJson {
      pageTitle
      topBanner {
        title
        description
        backgroundImage {
          publicURL
        }
      }
      contributePanel {
        title
        navButtons {
          title
          image {
            publicURL
          }
          component
          externalURL
          items {
            title
            tooltip
            options {
              name
              text
              url
              projects {
                code_name
                mascot_image_url
                name
                documentation_link
              }
            }
          }
        }
      }
      quickLinks {
        pretitle
        title
        columns {
          title
          links {
            title
            url
          }
        }
      }
      whereToStart {
        title
        description
        backgroundImage {
          publicURL
        }
        options {
          text
          url
        }
      }
      helpBanner {
        title
        description
        communityManagers {
          title
          list {
            user {
              id
              first_name
              last_name
              state
              country
              pic
              twitter
              linked_in
            }
          }
        }
        ambassadors {
          title
          list {
            user {
              first_name
              last_name
              state
              country
              pic
              twitter
            }
          }
        }
      }
      communityBanner {
        backgroundImage {
          publicURL
        }
        title
        description
        list {
          title
          links {
            text
            url
          }
        }
      }
    }
  }
`;

const CommunityPage = ({ data }) => (
  <CommunityPageTemplate data={data.communityJson} />
);

export const Head = ({ data }) => <title>{data.communityJson.pageTitle}</title>;

export default CommunityPage;
